import { css } from '@emotion/css';
import { useEffect, useState } from 'react';

import { type GrafanaTheme2 } from '@grafana/data';
import { t } from '@grafana/i18n';
import { ButtonGroup, Dropdown, Icon, Menu, ToolbarButton, useStyles2, type IconName } from '@grafana/ui';

import { isNewDashboard } from '../saving/shared';

import { type DashboardScene } from './DashboardScene';
import { dashboardModesEnabled, getDashboardMode, type DashboardMode } from './dashboardModes';

export function DashboardModePicker({ dashboard }: { dashboard: DashboardScene }) {
  const state = dashboard.useState();
  const styles = useStyles2(getStyles);
  const [buttonGroup, setButtonGroup] = useState<HTMLDivElement | null>(null);
  const [isCaretMenuOpen, setIsCaretMenuOpen] = useState(false);
  const mode = getDashboardMode(state);
  const nextMode = mode === 'view' ? 'edit' : 'view';
  const saveRequired = isNewDashboard(state);
  const disabled = dashboard.managedResourceCannotBeEdited();
  const hidden =
    !dashboardModesEnabled() ||
    !dashboard.canEditDashboard() ||
    (!state.editable && mode === 'view') ||
    state.editPanel ||
    state.editview ||
    state.viewPanel ||
    state.planning;
  useEffect(() => {
    if (!hidden && !disabled) {
      dashboard.trackModePickerShown();
    }
  }, [dashboard, hidden, disabled]);
  if (hidden) {
    return null;
  }

  const saveBeforeViewing = t('dashboard.modes.save-before-viewing', 'Save the dashboard before switching to Viewing.');

  const options: Array<{ value: DashboardMode; label: string; description: string; icon: IconName }> = [
    {
      value: 'view',
      label: t('dashboard.modes.view', 'Viewing'),
      description: saveRequired
        ? saveBeforeViewing
        : t('dashboard.modes.view-description', 'View and explore the dashboard.'),
      icon: 'eye',
    },
    {
      value: 'edit',
      label: t('dashboard.modes.edit', 'Editing'),
      description: t('dashboard.modes.edit-description', 'Manually edit panels, layout, and settings.'),
      icon: 'pen',
    },
  ];
  const current =
    mode === 'agent'
      ? { label: t('dashboard.modes.agent-edit', 'Agent editing'), icon: 'pen' as const }
      : options.find((option) => option.value === mode)!;

  const menu = (
    <div className={styles.menu}>
      <Menu ariaLabel={t('dashboard.modes.label', 'Dashboard mode')}>
        {options.map((option) => (
          <Menu.Item
            key={option.value}
            label={option.label}
            description={option.description}
            icon={option.icon}
            role="menuitemradio"
            ariaChecked={mode === option.value}
            disabled={!state.editable && option.value !== 'view'}
            className={styles.item}
            component={mode === option.value ? SelectedMode : undefined}
            onClick={() => dashboard.setDashboardMode(option.value, 'picker')}
          />
        ))}
      </Menu>
    </div>
  );

  return (
    <ButtonGroup ref={setButtonGroup}>
      <ToolbarButton
        variant="canvas"
        icon={current.icon}
        aria-label={t('dashboard.modes.current', 'Dashboard mode: {{mode}}', { mode: current.label })}
        tooltip={
          saveRequired && nextMode === 'view'
            ? saveBeforeViewing
            : nextMode === 'view'
              ? t('dashboard.modes.switch-to-viewing', 'Switch to Viewing')
              : t('dashboard.modes.switch-to-editing', 'Switch to Editing')
        }
        disabled={disabled || (!state.editable && nextMode === 'edit')}
        onClick={() => dashboard.setDashboardMode(nextMode, 'picker')}
      >
        {current.label}
      </ToolbarButton>
      <Dropdown
        positioningReference={buttonGroup}
        placement="bottom-end"
        onVisibleChange={(visible) => {
          setIsCaretMenuOpen(visible);
          if (visible) {
            dashboard.trackModePickerOpened();
          }
        }}
        overlay={menu}
      >
        <ToolbarButton
          variant="canvas"
          icon={isCaretMenuOpen ? 'angle-up' : 'angle-down'}
          aria-label={t('dashboard.modes.change-mode', 'Change dashboard mode')}
          aria-haspopup="menu"
          disabled={disabled}
        />
      </Dropdown>
    </ButtonGroup>
  );
}

function SelectedMode() {
  const styles = useStyles2(getStyles);
  return <Icon name="check" className={styles.check} aria-hidden />;
}

function getStyles(theme: GrafanaTheme2) {
  return {
    menu: css({ width: 360, maxWidth: 'calc(100vw - 32px)', '> [role="menu"]': { width: '100%' } }),
    item: css({ padding: theme.spacing(1.5, 5, 1.5, 1.5) }),
    check: css({ position: 'absolute', right: theme.spacing(1.5), top: theme.spacing(1.5) }),
  };
}
